import { theme } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Collapse, Divider, Empty, Flex, Input, Modal, Select, Tooltip, Typography } from 'zui/Antd';

// Import your per-panel content components
import { useGlobalSearchObjGenerator } from 'library/helpers/globalSearch';
import {
	RenderCategoriesPanelContent,
	RenderCredentialsPanelContent,
	RenderDeviceGroupsPanelContent,
	RenderDevicesPanelContent,
	RenderLockersPanelContent,
	RenderPeoplePanelContent,
	RenderTeamsPanelContent
} from './components';
import { SearchIcon } from 'assets/images/svg';

const { Text, Title } = Typography;

export default function SearchBarModal({ children, open, setOpen }) {
	const {
		token: { paddingMD, paddingSM, colorText, colorTextDescription, colorFill, colorBgContainer, fontWeightStrong }
	} = theme.useToken();

	const {
		dashboard: { selected }
	} = useSelector((state) => state.App);
	const formattedSelected = useMemo(() => {
		if (!selected) return '';
		return selected.charAt(0).toUpperCase() + selected.slice(1);
	}, [selected]);

	const inputRef = useRef(null);

	const [searchTerm, setSearchTerm] = useState('');
	const [selectedOption, setSelectedOption] = useState('all-areas');
	const [searchResults, setSearchResults] = useState([]);
	const [manualActiveKeys, setManualActiveKeys] = useState([]);
	const [autoActiveKeys, setAutoActiveKeys] = useState([]);

	//  Fuse Search Setup
	const searchFuses = useGlobalSearchObjGenerator();

	// Handle Cancel
	const handleCancel = () => setOpen(false);

	// Panel Configuration
	const panelConfigs = useMemo(() => {
		const common = [
			{
				label: 'People',
				key: 'people',
				fuseKey: 'People',
				Component: RenderPeoplePanelContent
			}
		];

		if (selected === 'lockers') {
			return [
				...common,
				{
					label: 'Lockers',
					key: 'lockers',
					fuseKey: 'Lockers',
					Component: RenderLockersPanelContent
				}
			];
		}

		if (selected === 'access') {
			return [
				...common,
				{
					label: 'Teams',
					key: 'teams',
					fuseKey: 'Teams',
					Component: RenderTeamsPanelContent
				},
				{
					label: 'Devices',
					key: 'devices',
					fuseKey: 'Devices',
					Component: RenderDevicesPanelContent
				},
				{
					label: 'Device Groups',
					key: 'device-groups',
					fuseKey: 'Device Groups',
					Component: RenderDeviceGroupsPanelContent
				},
				{
					label: 'Categories',
					key: 'categories',
					fuseKey: 'Categories',
					Component: RenderCategoriesPanelContent
				},
				{
					label: 'Credentials',
					key: 'credentials',
					fuseKey: 'Credentials',
					Component: RenderCredentialsPanelContent
				}
			];
		}

		// fallback: just people
		return common;
	}, [selected]);

	const selectOptions = [
		{ label: 'All Areas', value: 'all-areas' },
		...panelConfigs.map(({ label, key }) => ({
			label,
			value: key
		}))
	];

	const handleSearch = (value) => {
		if (!value.trim()) {
			setSearchResults([]);
			setAutoActiveKeys([]);
			return;
		}

		// Reset manual interaction when starting a new search
		setHasManuallyInteracted(false);
		setManualActiveKeys([]);

		const results = [];
		const activeKeys = [];

		panelConfigs.forEach(({ fuseKey }, index) => {
			const fuse = searchFuses[fuseKey];
			if (fuse) {
				const searchResult = fuse.search(value);
				results.push({ [fuseKey]: searchResult });

				// Only expand panels that have results
				if (searchResult.length > 0) {
					activeKeys.push(index);
				}
			}
		});

		setSearchResults(results);
		setAutoActiveKeys(activeKeys);
	};

	const handleSearchChange = (e) => {
		const value = e.target.value;
		setSearchTerm(value);
		handleSearch(value);
	};

	const panelsToShow = useMemo(() => {
		const basePanels =
			selectedOption === 'all-areas' ? panelConfigs : panelConfigs.filter((cfg) => cfg.key === selectedOption);

		if (searchTerm.trim() && searchResults.length > 0) {
			return basePanels.filter((panel) => {
				const panelResults = searchResults.find((result) => Object.keys(result)[0] === panel.fuseKey);
				return panelResults && panelResults[panel.fuseKey]?.length > 0;
			});
		}

		return basePanels;
	}, [selectedOption, panelConfigs, searchTerm, searchResults]);

	const [hasManuallyInteracted, setHasManuallyInteracted] = useState(false);

	const activeKeys = useMemo(() => {
		// If user has manually interacted, always use manual keys (even if empty)
		if (hasManuallyInteracted) {
			return manualActiveKeys;
		}

		if (!searchTerm.trim()) {
			return panelsToShow.map((_, index) => index);
		}

		const keysWithResults = [];
		panelsToShow.forEach((panel, index) => {
			const panelResults = searchResults.find((result) => Object.keys(result)[0] === panel.fuseKey);
			if (panelResults && panelResults[panel.fuseKey]?.length > 0) {
				keysWithResults.push(index);
			}
		});
		return keysWithResults;
	}, [manualActiveKeys, searchTerm, searchResults, panelsToShow, hasManuallyInteracted]);

	useEffect(() => {
		if (panelConfigs.length > 0 && autoActiveKeys.length === 0 && !searchTerm.trim()) {
			const allPanelKeys = panelConfigs.map((_, index) => index);
			setAutoActiveKeys(allPanelKeys);
		}
	}, [panelConfigs, autoActiveKeys.length, searchTerm]);

	// Keyboard shortcut handler
	useEffect(() => {
		const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);

		const handler = (e) => {
			const target = e.target || e.srcElement;
			// Check if the target is not an input or textarea
			if (!(target.tagName.toUpperCase() === 'INPUT' || target.tagName.toUpperCase() === 'TEXTAREA')) {
				// Show the modal if Cmd+K (for Mac) or Ctrl+K (for Windows) is pressed
				if ((isMac && e.metaKey && e.code === 'KeyK') || (!isMac && e.ctrlKey && e.code === 'KeyK')) {
					setOpen(true);
					e.preventDefault();
				}
			}
		};

		window.addEventListener('keydown', handler, true);
		return () => window.removeEventListener('keydown', handler, true);
	}, [setOpen]);

	// Clear search when modal closes
	useEffect(() => {
		if (!open) {
			setSearchTerm('');
			setSearchResults([]);
			setManualActiveKeys([]);
			setHasManuallyInteracted(false);
		}
	}, [open]);

	useEffect(() => {
		if (open && inputRef.current) {
			setTimeout(() => inputRef.current?.focus(), 0); // delay for mount event
		}
	}, [open]);

	return (
		<>
			{/* Trigger Button */}
			<Tooltip title={`Search Zezamii ${formattedSelected}`} placement='top'>
				{children ? (
					children
				) : (
					<Button icon={<SearchIcon color={colorTextDescription} />} onClick={() => setOpen(true)}>
						Search Zezamii {formattedSelected}
					</Button>
				)}
			</Tooltip>

			<Modal
				title={
					<Flex align='center'>
						<Input
							ref={inputRef}
							placeholder={`Search Zezamii ${formattedSelected}`}
							value={searchTerm}
							onChange={handleSearchChange}
							prefix={<SearchIcon style={{ marginRight: 8 }} />}
							style={{ flexGrow: 1, border: 'none', color: colorText, boxShadow: 'none' }}
							suffix={
								<Text style={{ color: colorTextDescription, fontWeight: 500 }}>Ctrl K / Cmd K</Text>
							}
						/>

						<Divider type='vertical' style={{ height: 30 }} />

						<Select
							options={selectOptions}
							value={selectedOption}
							onChange={(val) => {
								setSelectedOption(val);
								// Re-run search with new selection
								if (searchTerm.trim()) {
									handleSearch(searchTerm);
								}
							}}
							dropdownStyle={{ width: 200 }}
							placement='bottomRight'
							variant='borderless'
							fontWeight={fontWeightStrong}
						/>
					</Flex>
				}
				open={open}
				onCancel={handleCancel}
				centered
				closable={false}
				width={1492}
				styles={{
					header: {
						position: 'sticky',
						top: 0,
						zIndex: 10,
						background: colorBgContainer,
						padding: `${paddingMD}px 25px`,
						borderBottom: `1px solid ${colorFill}`,
						margin: 0
					},
					content: {
						padding: 0
					},
					body: {
						padding: paddingMD,
						height: 700,
						overflowY: 'auto'
					}
				}}
				footer={null}
			>
				{/* Search Results or All Panels */}
				{panelsToShow.length === 0 ? (
					<Flex align='center' justify='center' vertical style={{ height: '400px' }}>
						<Empty description={searchTerm.trim() ? 'No search results found' : 'No panels to display'} />
					</Flex>
				) : (
					<Collapse
						ghost
						activeKey={manualActiveKeys.length > 0 || hasManuallyInteracted ? manualActiveKeys : activeKeys}
						onChange={(keys) => {
							setManualActiveKeys(keys);
							setHasManuallyInteracted(true);
						}}
						size='large'
						items={panelsToShow.map(({ label, key, fuseKey, Component }, index) => {
							const panelResults = searchTerm.trim()
								? searchResults.find((result) => Object.keys(result)[0] === fuseKey)
								: null;

							return {
								key: index,
								label: (
									<Title
										style={{
											margin: 0,
											lineHeight: '20px'
										}}
										level={4}
									>
										{label}
									</Title>
								),
								children: Component ? (
									<Component
										searchResults={panelResults?.[fuseKey] || null}
										isSearching={!!searchTerm.trim()}
									/>
								) : null,
								style: {
									marginBottom: paddingSM,
									width: '100%'
								}
							};
						})}
					/>
				)}
			</Modal>
		</>
	);
}
